import React from 'react';
import { forwardRef } from '@shared/utils/forwardRef';
import { Flex, TextV2 } from '@withjoy/joykit';
import { ReactComponent as UnionIcon } from '@assets/icons/union-quick-add.svg';
import {
  ProductTitleText,
  quickAddButtonOverrides,
  StyledFlex,
  StyledIcon,
  StyledProductPhoto,
  StyledProductTile,
  StyledQuickAddButton,
  styles
} from './BaseProductDetails.styles';
import { addRendition } from '@shared/utils/photoRendition';
import { BaseProductDetailsProps } from './BaseProductDetails.types';
import { SwatchColorGrid } from '../../../../../SwatchColorGrid';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { ProductStorefrontStatusBadge } from '../../../ProductDetails/components/ProductStorefrontStatusBadge';
import { ProductCatalogItemType, ProductCatalogStorefrontStatus } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { SwatchColorOption } from '@apps/registry/common/components/SwatchColorGrid/types';
import { StarRatingWithLabel } from '../../../ProductDetails/components/ProductRating';
import { customRounding } from '@shared/utils/numbers';
import { CatalogProductItemTags } from '../../../../Catalog.types';

const LazyLoadingStyledProductPhoto = withLazyPropOnIntersection(StyledProductPhoto, 'photoUrl');

export const BaseProductDetails = React.memo(
  forwardRef<'div', BaseProductDetailsProps>((props, ref) => {
    const {
      onDetailClick,
      onDetailKeyPress,
      isAddLoading,
      productDisplayFields,
      isMobile,
      isHovered,
      isQuickAdd,
      isGroupItem,
      onViewMoreDimensionClick,
      onChangeActiveDimensionOption,
      onQuickAddClick,
      swatchColorOptions,
      preload,
      storefrontInfo,
      hasAnyStorefrontWarning,
      variant,
      initiallySelectedProduct,
      productRating,
      formatPrice,
      ...restProps
    } = props;

    const { id, brand, title, photoUrl, price, fullPrice, isFullPrice: isSingleFullPrice, tags } = productDisplayFields;

    const storefrontStatus = storefrontInfo?.storefrontStatus;

    const { t } = useTranslation('catalogRegistry');
    const { t: translations } = useTranslation('catalogRegistry');
    const { message } = translations('salesPrice');
    const { priceRange } = t('productTile');
    const { joyExclusive } = t('productDetailPage');

    const isCashFundType = initiallySelectedProduct?.itemType === ProductCatalogItemType.cash;
    const joyExclusiveLabel = tags.includes(CatalogProductItemTags.joyExclusive) ? joyExclusive() : null;
    const isGroupItemAndNotHovered = isGroupItem && !isHovered;
    const initialTilePresentation = initiallySelectedProduct?.tilePresentation;

    const displayTitle = isGroupItemAndNotHovered ? initialTilePresentation?.title : title;
    const displayPhotoUrl = isGroupItemAndNotHovered ? initialTilePresentation?.photos[0]?.url : photoUrl;
    const isPriceRange = isGroupItemAndNotHovered ? !initialTilePresentation?.fullPriceRange?.isSinglePrice : false;
    const displayPrice = isGroupItemAndNotHovered ? initialTilePresentation?.fullPriceRange?.lowerPrice : fullPrice;
    const salePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.lowerPrice : price;
    const displayUpperPrice = isGroupItemAndNotHovered ? initialTilePresentation?.fullPriceRange?.upperPrice : null;
    const upperSalePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.upperPrice : fullPrice;
    const lowerSalePrice = isGroupItemAndNotHovered ? initialTilePresentation?.priceRange?.lowerPrice : null;
    const isFullPriceRange = isGroupItemAndNotHovered ? initialTilePresentation?.isFullPrice : isSingleFullPrice;

    const formattedPrice = formatPrice(displayPrice);
    const formattedUpperPrice = formatPrice(displayUpperPrice);
    const formattedUpperSalePrice = formatPrice(upperSalePrice);
    const formattedLowerSalePrice = formatPrice(lowerSalePrice);
    const salePriceValue = formatPrice(salePrice);

    const onScreenPhotoUrl = addRendition({ url: displayPhotoUrl, renditionSize: 'medium', isProduct: true });
    const isGridItem = variant === 'gridItem';
    const hasDarkBackground = variant === 'spotlightCarouselItem' && !isHovered;
    const showColorSwatch = swatchColorOptions?.length > 1;

    const handleInteract = (e: React.SyntheticEvent, option: SwatchColorOption) => {
      if (isMobile && e.type === 'click') {
        onQuickAddClick?.(e as React.MouseEvent, option.id);
        return;
      }
      onChangeActiveDimensionOption(option);
    };

    return (
      <StyledProductTile tabIndex={0} id={id} data-id={id} onKeyPress={onDetailKeyPress} onClick={onDetailClick} ref={ref} {...restProps}>
        {hasAnyStorefrontWarning && (
          <Flex __css={styles.storefrontHeadContainer}>
            {storefrontStatus === ProductCatalogStorefrontStatus.comingSoon && <ProductStorefrontStatusBadge storefrontStatus={storefrontStatus} variant="head" type="title" />}
          </Flex>
        )}
        <StyledFlex flexDirection="column" __css={{ cursor: 'pointer ' }}>
          {joyExclusiveLabel && (
            <TextV2 as="span" color="purple6" textAlign="center" typographyVariant="hed2" fontSize={500} marginBottom={3}>
              {joyExclusiveLabel}
            </TextV2>
          )}
          <StyledFlex
            backgroundColor={'white'}
            justifyContent="center"
            alignItems="center"
            __css={styles.photoWrapper}
            width={isQuickAdd ? '70%' : '100%'}
            margin={isQuickAdd ? 'auto' : 'initial'}
            paddingTop={isQuickAdd ? '75%' : '100%'}
          >
            <LazyLoadingStyledProductPhoto
              photoUrl={{
                onscreen: onScreenPhotoUrl,
                offscreen: preload ? onScreenPhotoUrl : undefined
              }}
            />
          </StyledFlex>
          {productRating && (
            <StarRatingWithLabel
              id={id}
              visibility={productRating.averageRating ? 'visible' : 'hidden'}
              totalReviewCount={productRating.totalReviewCount}
              labelVariant={isMobile ? 'averageRatingWithCountShort' : 'averageRatingWithCount'}
              justifyContent={isGridItem ? 'center' : 'flex-start'}
              marginTop={6}
              rating={customRounding(productRating.averageRating) || 0}
              showPartialRating={true}
              iconSize={14}
            />
          )}
          <Flex flexDirection="row" alignItems="flex-start" marginTop={5} justifyContent={isGridItem ? 'center' : 'space-between'} paddingX={[3, null, 0]}>
            <StyledFlex flexDirection="column" alignItems={isGridItem ? 'center' : 'flex-start'} __css={styles.flexTextWrapper}>
              {brand && (
                <TextV2 as="span" typographyVariant="body1" __css={hasDarkBackground ? styles.brandTextDarkBg : styles.brandText}>
                  {brand}
                </TextV2>
              )}
              {displayTitle && (
                <ProductTitleText
                  typographyVariant="hed2"
                  $variant={variant}
                  fontSize={500}
                  title={displayTitle}
                  __css={hasDarkBackground ? styles.productTitleDarkBg : styles.productTitle}
                >
                  {displayTitle}
                </ProductTitleText>
              )}

              {displayPrice && (
                <Flex justifyContent={'center'} flexDirection={(isPriceRange && 'column') || 'row'} alignItems={(!isCashFundType && isPriceRange && 'center') || ''} gap="2">
                  <TextV2
                    as="span"
                    color={hasDarkBackground ? 'white' : 'mono10'}
                    textDecoration={!isFullPriceRange ? 'line-through' : ''}
                    typographyVariant="label6"
                    alignContent="center"
                  >
                    {isPriceRange
                      ? priceRange({
                          lowerBound: formattedPrice,
                          upperBound: formattedUpperPrice
                        })
                      : formattedPrice}
                  </TextV2>
                  {!isFullPriceRange && (
                    <TextV2 as="span" color="#D80B0B" typographyVariant="label6" alignContent="center">
                      {message()}{' '}
                      {(!isPriceRange && salePriceValue) ||
                        priceRange({
                          lowerBound: formattedLowerSalePrice,
                          upperBound: formattedUpperSalePrice
                        })}
                    </TextV2>
                  )}
                </Flex>
              )}
            </StyledFlex>
            {!isGridItem && !isHovered && !isMobile && (
              <StyledFlex>
                <StyledIcon>
                  <UnionIcon />
                </StyledIcon>
              </StyledFlex>
            )}
          </Flex>
          {showColorSwatch && !isHovered && (
            <Flex justifyContent={isGridItem ? 'center' : 'flex-start'} marginTop={7} paddingX={[4, null, 0]} maxWidth="100%" __css={!isHovered ? {} : styles.hovered}>
              <SwatchColorGrid
                alignment={'center'}
                swatchSize={6}
                rows={1}
                columns={[4, null, 4]}
                options={swatchColorOptions}
                interactionType={'click'}
                onInteract={handleInteract}
                onViewMoreClick={onViewMoreDimensionClick}
                hasDarkBackground={hasDarkBackground}
              />
            </Flex>
          )}
          {isMobile && !isQuickAdd && (
            <Flex marginTop={2} marginBottom={6} justifyContent={isGridItem ? 'center' : 'flex-start'} paddingX={3}>
              <StyledQuickAddButton
                startIcon={UnionIcon}
                loading={isAddLoading}
                variant="link"
                overrides={quickAddButtonOverrides}
                onClick={e => {
                  e.preventDefault();
                  onQuickAddClick && onQuickAddClick(e);
                }}
                typographyVariant={'label2'}
                marginTop={2}
                disabled={!storefrontInfo?.canAddToRegistry}
                $darkBg={hasDarkBackground}
              >
                Quick Add
              </StyledQuickAddButton>
            </Flex>
          )}
        </StyledFlex>
      </StyledProductTile>
    );
  })
);
BaseProductDetails.displayName = 'BaseProductDetailsV2';
